import React, { useState, useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import {
  Box,
  Button,
  Typography,
  ButtonGroup
} from '@mui/material';
import {
  Print as PrintIcon,
  QrCode as QrCodeIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import '../styles/certificate.css';
import { useOrg } from '../context/OrgContext';

const PrintCertificate = ({ permit, onClose }) => {
  const { t } = useTranslation();
  const { org } = useOrg() || {};
  const orgName = org?.name || t('printCertificate.defaultOrgName');
  const [printMode, setPrintMode] = useState('certificate');
  const qrCodeRef = useRef();

  if (!permit) return null;

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const calculateDaysRemaining = () => {
    const today = new Date();
    const validTo = new Date(permit.valid_to);
    const timeDiff = validTo - today;
    const daysRemaining = Math.ceil(timeDiff / (1000 * 3600 * 24));
    return daysRemaining > 0 ? daysRemaining : 0;
  };

  // Generate QR code data (encode permit information)
  const qrCodeData = `PERMIT|${permit.permit_number}|${permit.vehicle_number}|${permit.owner_name}|${permit.valid_to}`;

  // Download QR Code
  const downloadQRCode = () => {
    if (qrCodeRef.current) {
      const canvas = qrCodeRef.current.querySelector('canvas');
      const image = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = image;
      link.download = `QR-Permit-${permit.permit_number}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div className="print-certificate-container">
      {/* Print Header with Options */}
      <Box sx={{
        display: 'flex',
        gap: 2,
        mb: 3,
        p: 2,
        bgcolor: '#f5f5f5',
        borderRadius: 1,
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        className: 'no-print'
      }} className="no-print">
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            {t('printCertificate.printOptions')}
          </Typography>
          <ButtonGroup variant="outlined" size="small">
            <Button
              onClick={() => setPrintMode('certificate')}
              variant={printMode === 'certificate' ? 'contained' : 'outlined'}
              sx={{
                backgroundColor: printMode === 'certificate' ? '#1976d2' : 'white',
                color: printMode === 'certificate' ? 'white' : 'inherit'
              }}
            >
              {t('printCertificate.certificateOnly')}
            </Button>
            <Button
              onClick={() => setPrintMode('qr')}
              variant={printMode === 'qr' ? 'contained' : 'outlined'}
              startIcon={<QrCodeIcon />}
              sx={{
                backgroundColor: printMode === 'qr' ? '#1976d2' : 'white',
                color: printMode === 'qr' ? 'white' : 'inherit'
              }}
            >
              {t('printCertificate.qrCodeOption')}
            </Button>
          </ButtonGroup>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={() => window.print()}
            variant="contained"
            startIcon={<PrintIcon />}
            sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#45a049' } }}
          >
            {t('printCertificate.print')}
          </Button>
          <Button
            onClick={onClose}
            variant="outlined"
            startIcon={<CloseIcon />}
            color="error"
          >
            {t('common.close')}
          </Button>
        </Box>
      </Box>

      {/* Certificate Content */}
      <div className="certificate-wrapper">
        {/* Main Certificate Border */}
        <div className="certificate">
          {/* Header Section */}
          <div className="certificate-header-section">
            <div className="certificate-emblem">🏛️</div>
            <div className="certificate-title">
              <h1>{t('printCertificate.certificateTitle')}</h1>
              <p>{orgName}</p>
            </div>
            <div className="certificate-code">
              <strong>{t('printCertificate.permitPrefix', { number: permit.permit_number })}</strong>
            </div>
          </div>

          {/* Status Badge */}
          <div className={`status-badge status-${permit.status}`}>
            {t(`common.${permit.status}`, { defaultValue: permit.status }).toUpperCase()}
          </div>

          {/* Certificate Body */}
          <div className="certificate-body">
            <p className="certificate-intro">
              {t('printCertificate.certifyText', { owner: permit.owner_name, org: orgName })}
            </p>

            {/* Permit Details Grid */}
            <div className="details-grid">
              <div className="detail-row">
                <div className="detail-item">
                  <label>{t('printCertificate.permitTypeLabel')}</label>
                  <div className="detail-value">{permit.permit_type?.name || t('permitSearch.na')}</div>
                </div>
                <div className="detail-item">
                  <label>{t('printCertificate.permitCodeLabel')}</label>
                  <div className="detail-value">{permit.permit_type?.code || t('permitSearch.na')}</div>
                </div>
              </div>

              <div className="detail-row">
                <div className="detail-item">
                  <label>{t('printCertificate.vehicleNumberLabel')}</label>
                  <div className="detail-value">{permit.vehicle_number}</div>
                </div>
                <div className="detail-item">
                  <label>{t('printCertificate.vehicleTypeLabel')}</label>
                  <div className="detail-value">{permit.vehicle_type?.name || t('permitSearch.na')}</div>
                </div>
              </div>

              <div className="detail-row">
                <div className="detail-item">
                  <label>{t('printCertificate.ownerNameLabel')}</label>
                  <div className="detail-value">{permit.owner_name}</div>
                </div>
                <div className="detail-item">
                  <label>{t('printCertificate.ownerCnicLabel')}</label>
                  <div className="detail-value">{permit.owner_cnic || t('permitSearch.na')}</div>
                </div>
              </div>

              <div className="detail-row">
                <div className="detail-item">
                  <label>{t('printCertificate.ownerPhoneLabel')}</label>
                  <div className="detail-value">{permit.owner_phone}</div>
                </div>
                <div className="detail-item">
                  <label>{t('printCertificate.authorityLabel')}</label>
                  <div className="detail-value">{permit.authority}</div>
                </div>
              </div>

              {permit.vehicle_make && (
                <div className="detail-row">
                  <div className="detail-item">
                    <label>{t('printCertificate.vehicleMakeLabel')}</label>
                    <div className="detail-value">{permit.vehicle_make}</div>
                  </div>
                  <div className="detail-item">
                    <label>{t('printCertificate.vehicleModelLabel')}</label>
                    <div className="detail-value">{permit.vehicle_model || t('permitSearch.na')}</div>
                  </div>
                </div>
              )}

              {permit.vehicle_year && (
                <div className="detail-row">
                  <div className="detail-item">
                    <label>{t('printCertificate.vehicleYearLabel')}</label>
                    <div className="detail-value">{permit.vehicle_year}</div>
                  </div>
                  <div className="detail-item">
                    <label>{t('printCertificate.vehicleCapacityLabel')}</label>
                    <div className="detail-value">{permit.vehicle_capacity || t('permitSearch.na')}</div>
                  </div>
                </div>
              )}
            </div>

            {/* Validity Period */}
            <div className="validity-section">
              <h3>{t('printCertificate.validityPeriod')}</h3>
              <div className="validity-grid">
                <div className="validity-item">
                  <label>{t('printCertificate.validFromLabel')}</label>
                  <div className="validity-date">{formatDate(permit.valid_from)}</div>
                </div>
                <div className="validity-item">
                  <label>{t('printCertificate.validToLabel')}</label>
                  <div className="validity-date">{formatDate(permit.valid_to)}</div>
                </div>
                <div className="validity-item">
                  <label>{t('printCertificate.daysRemainingLabel')}</label>
                  <div className="validity-date">{t('permitSearch.daysUnit', { count: calculateDaysRemaining() })}</div>
                </div>
              </div>
            </div>

            {/* Additional Info */}
            {permit.description && (
              <div className="info-section">
                <h3>{t('printCertificate.descriptionSection')}</h3>
                <p>{permit.description}</p>
              </div>
            )}

            {permit.approved_routes && (
              <div className="info-section">
                <h3>{t('printCertificate.approvedRoutesSection')}</h3>
                <p>{permit.approved_routes}</p>
              </div>
            )}

            {permit.restrictions && (
              <div className="info-section">
                <h3>{t('printCertificate.restrictionsSection')}</h3>
                <p>{permit.restrictions}</p>
              </div>
            )}
          </div>

          {/* Footer Section */}
          <div className="certificate-footer">
            <div className="footer-left">
              <p>{t('printCertificate.issuedDateLabel')} <strong>{formatDate(permit.issued_date)}</strong></p>
            </div>
            <div className="footer-right">
              <p>{t('printCertificate.certificateIdLabel')} <strong>{permit.id}</strong></p>
            </div>
          </div>

          {/* Seal & Signature Area */}
          <div className="signature-section">
            <div className="seal">🔓</div>
            <div className="sig-line">
              <p>{t('printCertificate.authorizedBy')}</p>
              <div className="sig-box">{orgName}</div>
            </div>
          </div>

          {/* QR Code Section - Visible when printMode includes QR */}
          {printMode === 'qr' && (
            <div className="qr-section">
              <h3>{t('printCertificate.qrSectionTitle')}</h3>
              <div className="qr-code-container" ref={qrCodeRef}>
                <QRCodeCanvas
                  value={qrCodeData}
                  size={200}
                  level="H"
                  includeMargin={true}
                  fgColor="#1976d2"
                  bgColor="#ffffff"
                />
                <div className="permit-number">{t('printCertificate.permitColonLabel', { number: permit.permit_number })}</div>
              </div>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 2 }} className="no-print">
                <Button
                  startIcon={<DownloadIcon />}
                  size="small"
                  variant="outlined"
                  onClick={downloadQRCode}
                  sx={{ fontSize: '11px' }}
                >
                  {t('printCertificate.downloadQr')}
                </Button>
              </Box>
              <p>{t('printCertificate.scanToVerify')}</p>
              <p style={{ fontSize: '10px', color: '#42a5f5', marginTop: '10px' }}>
                {t('printCertificate.encodedData', { data: qrCodeData })}
              </p>
            </div>
          )}

          {/* Legal Notice */}
          <div className="legal-notice">
            <p>{t('printCertificate.legalNotice')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintCertificate;
